import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert, Row, Col, Input, Label } from 'reactstrap';
import { axios } from 'ApiClient';
import Hook from '../../../../../shared/hooks';
import ReactTableBase from '../../../../../shared/tables/table/ReactTableBase';
import formater from '../../../../../shared/helpers/WVFormatter';

export const modalId = 'podcastPlacementsModal';

// Date-only placement fields are stored as UTC midnight, so they must be read back in UTC —
// plain moment() renders in the viewer's zone and shows the previous day west of Greenwich.
const formatDate = value => formater.formatDateInUTC(value, 'MM/DD/YY') || '-';

const isEligibleForPayment = (placement) => {
  const paymentStatus = placement.paidStatus || placement.paymentStatus || 'new';
  return placement.actualRunDate !== null
    && placement.actualRunDate !== undefined
    && ['new'].includes(paymentStatus);
};

const tableConfig = {
  isEditable: false,
  isResizable: false,
  isSelectable: false,
  useFlex: true,
  isSortable: true,
  withPagination: true,
  manualPageSize: [10, 20, 50, 100],
  withSearchEngine: false,
};

const columnTransform = [
  {
    id: 'showName',
    Header: 'Show Name',
    accessor: row => row.showName || '',
    Cell: ({ row }) => <span>{row.original.showName || '-'}</span>,
    width: 200,
  },
  {
    id: 'bookedImpressions',
    Header: 'Booked Impressions',
    accessor: row => Number(row.bookedImpressions || 0),
    Cell: ({ row }) => <span>{Number(row.original.bookedImpressions || 0).toLocaleString()}</span>,
    width: 150,
  },
  {
    id: 'netCost',
    Header: 'Contract Amount',
    accessor: row => Number(row.netCost || 0),
    Cell: ({ row }) => <span>{formater.formatCurrency(Number(row.original.netCost || 0))}</span>,
    width: 120,
  },
  {
    id: 'costWithFee',
    Header: 'Total Contract Amount',
    accessor: row => Number(row.costWithFee || 0),
    Cell: ({ row }) => <span>{formater.formatCurrency(Number(row.original.costWithFee || 0))}</span>,
    width: 130,
  },
  {
    id: 'startDropDate',
    Header: 'Start Drop Date',
    accessor: row => row.startDropDate || '',
    Cell: ({ row }) => <span>{formatDate(row.original.startDropDate)}</span>,
    width: 120,
  },
  {
    id: 'endDate',
    Header: 'End Date',
    accessor: row => row.endDate || '',
    Cell: ({ row }) => <span>{formatDate(row.original.endDate)}</span>,
    width: 110,
  },
  {
    id: 'status',
    Header: 'Status',
    accessor: row => row.status || '',
    Cell: ({ row }) => <span>{row.original.status || '-'}</span>,
    width: 110,
  },
  {
    id: 'paymentStatus',
    Header: 'Payment Status',
    accessor: row => row.paymentStatus || 'new',
    Cell: ({ row }) => {
      const value = row.original.paymentStatus || 'new';
      const color = value === 'paid' ? '#28a745' : '#6c757d';
      return (
        <span
          style={{
            backgroundColor: color,
            color: 'white',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '12px',
            textTransform: 'capitalize',
          }}
        >
          {value}
        </span>
      );
    },
    width: 120,
  },
];

const PodcastPlacementsModal = () => {
  const { data: dataModal, showModal: isShow, close } = Hook.useModalHook(modalId);
  const [loading, setLoading] = useState(false);
  const [placements, setPlacements] = useState([]);
  const [ioNumber, setIoNumber] = useState(null);
  const [error, setError] = useState(null);
  const [showNameFilter, setShowNameFilter] = useState('');
  const [allowedToPayOnly, setAllowedToPayOnly] = useState(false);

  useEffect(() => {
    const fetchPlacements = async () => {
      if (!isShow || !dataModal?.dealId) return;

      setLoading(true);
      setError(null);
      setPlacements([]);
      setIoNumber(dataModal.ioNumber || null);
      setShowNameFilter('');
      setAllowedToPayOnly(false);

      try {
        const res = await axios.get(`/api/portal/finance/get-podcast-placements?dealId=${encodeURIComponent(dataModal.dealId)}`);
        if (!res.data?.success) {
          throw new Error(res.data?.error || 'Unable to load podcast placements');
        }
        setPlacements(res.data.placements || []);
        setIoNumber(res.data.ioNumber || dataModal.ioNumber || null);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPlacements();
  }, [isShow, dataModal?.dealId]);

  const title = useMemo(() => {
    if (ioNumber) return `Podcast Placements - ${ioNumber}`;
    return 'Podcast Placements';
  }, [ioNumber]);

  const filteredPlacements = useMemo(() => {
    const q = showNameFilter.trim().toLowerCase();
    return placements.filter((placement) => {
      if (allowedToPayOnly && !isEligibleForPayment(placement)) {
        return false;
      }
      if (!q) {
        return true;
      }
      return (placement.showName || '').toLowerCase().includes(q);
    });
  }, [placements, showNameFilter, allowedToPayOnly]);

  const totals = useMemo(() => filteredPlacements.reduce((acc, p) => ({
    netCost: acc.netCost + Number(p.netCost || 0),
    costWithFee: acc.costWithFee + Number(p.costWithFee || 0),
    bookedImpressions: acc.bookedImpressions + Number(p.bookedImpressions || 0),
  }), { netCost: 0, costWithFee: 0, bookedImpressions: 0 }), [filteredPlacements]);

  return (
    <Modal
      isOpen={isShow}
      toggle={close}
      size="lg"
      style={{ maxWidth: '1300px' }}
    >
      <ModalHeader toggle={close} tag="h4">
        {title}
      </ModalHeader>
      <ModalBody>
        <div className="theme-light">
          {loading && (
            <div className="text-center p-4">
              <Spinner color="primary" />
            </div>
          )}
          {!loading && error && (
            <Alert color="danger">{error}</Alert>
          )}
          {!loading && !error && !ioNumber && (
            <Alert color="warning">This deal does not have a Podcast IO number.</Alert>
          )}
          {!loading && !error && ioNumber && placements.length === 0 && (
            <Alert color="warning">No podcast placements found for this IO number.</Alert>
          )}
          {!loading && !error && placements.length > 0 && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <div className="p-2 border rounded text-center">
                    <div className="font-weight-bold text-muted small">Total Booked Impressions</div>
                    <div style={{ fontSize: '18px' }}>{Number(totals.bookedImpressions).toLocaleString()}</div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-2 border rounded text-center">
                    <div className="font-weight-bold text-muted small">Contract Amount</div>
                    <div style={{ fontSize: '18px' }}>{formater.formatCurrency(totals.netCost)}</div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-2 border rounded text-center">
                    <div className="font-weight-bold text-muted small">Total Contract Amount</div>
                    <div style={{ fontSize: '18px' }}>{formater.formatCurrency(totals.costWithFee)}</div>
                  </div>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Label className="mb-1 font-weight-bold">Search by Show Name</Label>
                  <Input
                    type="text"
                    value={showNameFilter}
                    onChange={e => setShowNameFilter(e.target.value)}
                    placeholder="Show Name..."
                  />
                </Col>
                <Col md={6} className="d-flex align-items-end">
                  <Label check className="mb-2 font-weight-bold">
                    <Input
                      type="checkbox"
                      checked={allowedToPayOnly}
                      onChange={e => setAllowedToPayOnly(e.target.checked)}
                    />
                    {' '}
                    Allowed to pay
                  </Label>
                </Col>
              </Row>
              <Row>
                <Col>
                  <ReactTableBase
                    key={`podcastPlacementsModal-${filteredPlacements.length}`}
                    id="podcastPlacementsModal"
                    columns={columnTransform}
                    data={filteredPlacements}
                    tableConfig={tableConfig}
                  />
                </Col>
              </Row>
            </>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={close}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

export default PodcastPlacementsModal;
